import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import {
  Container,
  Section,
  SectionHead,
  Card,
  fadeUp,
  stagger,
} from "@comai-portal/design-system";
import { PROMPTS_PAGE_DATA } from "../generated/prompts-page-data";
import { PAGE_HEROES } from "../content/page-copy";
import {
  fetchLiveLibrary,
  fetchPromptReviews,
  type LiveLibrary,
  type LivePromptState,
  type PublicReview,
} from "../services/promptLibraryLive";

const HERO = PAGE_HEROES["prompts"]!;

/**
 * PromptsPage — the prompt library, rendered from canon. (CO 530, CO 543)
 *
 * The data module is GENERATED from the federation-comos-profiles tenant
 * canon (verify/co-530-prompts-page.json → prompts-page-data.ts), and the
 * builder prompt that produced this page is itself a library entry
 * (candidate-008). Statuses pass through exactly as the canon records them —
 * proven, untested, and gate-held are never blurred. Listing-level fields
 * only; the prompt texts stay in the library.
 *
 * CO 543 added the facets and these controls. Sort and filter state lives in
 * the URL rather than component state, so a filtered view is a shareable link
 * and the back button works (the CO 540 blog-catalog pattern).
 *
 * ⚠️ Which facets get controls is a MEASURED decision, not a taste one. Of the
 * runs-against facets CO 543 recovered from the intake files, `door` is
 * carried by all ten library entries but `platform` and `scope` by only two —
 * so filtering a ten-item list by platform would be a control that mostly
 * returns nothing. The controls are class / door / status-by-section; the
 * sparse facets are rendered on the card, where they inform without
 * pretending to be navigable. When more candidates are authored naming a
 * platform, that is the moment to promote it to a control.
 */

/**
 * One card's data. Structural rather than the generated module's literal type,
 * because a card can also be a prompt written outside ComOS, which arrives
 * from the live read and was never in the build (CO 613).
 */
interface Entry {
  id: string | null;
  title: string;
  /** What the prompt does, in a sentence or two. Null until one is written. */
  description?: string | null;
  class: string;
  grade: string | null;
  status: string | null;
  sha16: string | null;
  category: string;
  admitted: string | null;
  listing: { price: number; validity_days: number | null } | null;
  platform: string | null;
  scope: string | null;
  tools: readonly string[];
  door: string | null;
  body: string | null;
  withheld: string | null;
  manifest: {
    tools: readonly string[];
    scopes: readonly string[];
    step_up: readonly string[];
    effect: string;
  } | null;
  /** The pseudonym of an outside author; absent for ComOS's own prompts. */
  author?: string | null;
  /** The bench run an outside prompt was listed on. */
  tested?: { release: string; calls: number; at: string } | null;
}

const BUILT_ENTRIES = PROMPTS_PAGE_DATA.entries as unknown as readonly Entry[];
const LIBRARY_ENTRIES = BUILT_ENTRIES.filter(
  (e) => e.category !== "prompt:runtime-source",
);
const RUNTIME_ENTRIES = BUILT_ENTRIES.filter(
  (e) => e.category === "prompt:runtime-source",
);

/** Canon status verbatim; a null status is a runtime-source prompt held by
 * the CO-527 drift gate — rendered as gate-held, never as anything stronger. */
function statusOf(e: Entry): "proven" | "untested" | "gate-held" | string {
  return e.status ?? "gate-held";
}

/** Three visually distinct treatments — proven (teal), untested (amber),
 * gate-held (neutral). Never blurred into one look. */
function statusClasses(status: string): string {
  switch (status) {
    case "proven":
      return "border-flow/40 bg-flow-soft text-flow dark:border-flow-bright/40 dark:bg-flow/15 dark:text-flow-bright";
    case "untested":
      return "border-[#F59E0B]/50 bg-[#FEF3C7] text-[#92400E] dark:border-[#F59E0B]/40 dark:bg-[#F59E0B]/15 dark:text-[#FBBF24]";
    default:
      return "border-mute-line bg-mute-surface text-mute dark:border-ink-line dark:bg-ink-line dark:text-mute-soft";
  }
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`rounded-pill border px-2 py-0.5 text-[11px] font-grotesk font-semibold uppercase tracking-wide ${statusClasses(status)}`}
    >
      {status}
    </span>
  );
}

// ── the vocabulary: a documented SUBSET of what the tool serves ──────────────
// introspection_prompt_library also serves `class`/`status` sorts and
// platform/scope/batch/q filters. The page exposes what is useful over ten
// entries; the tool is the door for the rest.

const SORTS = [
  { value: "admitted_desc", label: "Newest first" },
  { value: "admitted_asc", label: "Oldest first" },
  { value: "title", label: "Title (A–Z)" },
  // CO 613 — by published reviews, read live. Unreviewed prompts follow the
  // reviewed ones in the default order; with no live data it is that order.
  { value: "reviews", label: "Best reviewed" },
] as const;
type Sort = (typeof SORTS)[number]["value"];
const DEFAULT_SORT: Sort = "admitted_desc";

const CLASSES = [
  { value: "", label: "All classes" },
  { value: "operational", label: "Operational" },
  { value: "meta", label: "Meta" },
  { value: "experience", label: "Experience" },
] as const;

const DOORS = [
  { value: "", label: "Any door" },
  { value: "connector", label: "MCP connector" },
  { value: "cockpit", label: "Cockpit" },
] as const;

const isSort = (v: string): v is Sort => SORTS.some((s) => s.value === v);

/** Undated entries sort LAST under both directions — "oldest first" is a claim
 * about dates, and an entry without one is unknown, not oldest. Mirrors the
 * ordering the served read applies, so page and tool agree. */
function compare(
  sort: Sort,
  live: Map<string, LivePromptState> | null = null,
): (a: Entry, b: Entry) => number {
  const byTitle = (a: Entry, b: Entry) => a.title.localeCompare(b.title);
  if (sort === "title") return byTitle;
  if (sort === "reviews") {
    const score = (e: Entry): [number, number] => {
      const r = e.id ? live?.get(e.id)?.reviews : undefined;
      return r && r.count > 0 ? [r.average, r.count] : [-1, 0];
    };
    const byDate = compare(DEFAULT_SORT);
    return (a, b) => {
      const [aa, ac] = score(a);
      const [ba, bc] = score(b);
      return ba - aa || bc - ac || byDate(a, b);
    };
  }
  return (a, b) => {
    if (!a.admitted && !b.admitted) return byTitle(a, b);
    if (!a.admitted) return 1;
    if (!b.admitted) return -1;
    const cmp =
      sort === "admitted_asc"
        ? a.admitted.localeCompare(b.admitted)
        : b.admitted.localeCompare(a.admitted);
    return cmp || byTitle(a, b);
  };
}

/**
 * CO 613 — the review line. There is no human session on this site, so a
 * review is written the way everything else is: through the reader's agent.
 */
export function reviewLine(id: string): string {
  return `Review the ComOS library prompt ${id}: ask me for a rating from 1 to 5 and a sentence or two on what I ran it for, then call federation_prompt_review with {"id": "${id}", "rating": <my rating>, "body": "<my words>"} on the ComOS connector (through federation_act if it is not in your tool list).`;
}

/** Live state laid over a built card: reviews, recorded runs, revocation. */
function LiveLine({ id, live }: { id: string; live: LivePromptState }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(reviewLine(id));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // A denied clipboard is a non-event; the line is not hidden behind it.
    }
  };
  const { reviews, runs } = live;
  const [list, setList] = useState<PublicReview[] | null | "failed">(null);
  const [open, setOpen] = useState(false);
  const toggleReviews = () => {
    const next = !open;
    setOpen(next);
    if (next && list === null) {
      fetchPromptReviews(id)
        .then(setList)
        .catch(() => setList("failed"));
    }
  };
  return (
    <div className="mt-2 text-xs text-mute dark:text-mute-soft" data-testid="prompt-live">
      {live.revoked && (
        <p className="mb-1 rounded border border-[#DC2626]/50 bg-[#FEE2E2] px-2 py-1 text-[#991B1B] dark:bg-[#DC2626]/15 dark:text-[#FCA5A5]">
          <span className="font-grotesk font-semibold">Revoked. </span>
          {live.revoked.reason} ComOS refuses to start a run of this version.
        </p>
      )}
      {live.stale && (
        <p className="mb-1">
          <span className="font-grotesk font-semibold">Stale. </span>
          This prompt names a tool ComOS no longer serves.
        </p>
      )}
      <p className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {reviews && reviews.count > 0 ? (
          <span>
            <span className="font-grotesk font-semibold text-ink dark:text-paper">
              {reviews.average.toFixed(1)} of 5
            </span>{" "}
            from {reviews.count} review{reviews.count === 1 ? "" : "s"}
            {reviews.verified_runs > 0
              ? `, ${reviews.verified_runs} by a reviewer with a recorded run`
              : ""}
          </span>
        ) : reviews ? (
          <span>No reviews yet</span>
        ) : null}
        {runs && runs.total > 0 && (
          <span>
            {runs.total} recorded run{runs.total === 1 ? "" : "s"}, {runs.clean} inside
            the manifest
          </span>
        )}
        <button
          type="button"
          onClick={copy}
          className="font-grotesk text-signal dark:text-signal-bright underline"
          aria-label={`Copy the review line for ${id}`}
        >
          {copied ? "Copied" : "Copy review line"}
        </button>
        {reviews && reviews.count > 0 && (
          <button
            type="button"
            onClick={toggleReviews}
            aria-expanded={open}
            className="font-grotesk text-signal dark:text-signal-bright underline"
          >
            {open ? "Hide reviews" : "Read reviews"}
          </button>
        )}
      </p>
      {open && list === "failed" && (
        <p className="mt-2">The reviews could not be read just now.</p>
      )}
      {open && Array.isArray(list) && (
        <ul className="mt-2 space-y-2" aria-label={`Reviews of ${id}`}>
          {list.map((r) => (
            <li
              key={r.review_id}
              className="rounded border border-mute-line dark:border-ink-line px-2 py-1.5"
            >
              <p>
                <span className="font-grotesk font-semibold text-ink dark:text-paper">
                  {r.rating} of 5
                </span>
                {r.title ? ` · ${r.title}` : ""}
              </p>
              {/* A review is outside text. Rendered as text only, never as markup. */}
              {r.body && <p className="mt-1 whitespace-pre-wrap">{r.body}</p>}
              <p className="mt-1">
                {r.reviewer}
                {r.verified_run ? " · has a recorded run of this version" : ""} ·{" "}
                {r.created_at.slice(0, 10)}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function EntryCard({ entry, live }: { entry: Entry; live?: LivePromptState }) {
  // Live state describes one version. If the canon has moved on since this
  // page was built, the reviews and runs are for text this card does not show.
  const sameVersion =
    live !== undefined &&
    live.sha256 !== null &&
    entry.sha16 !== null &&
    live.sha256.startsWith(entry.sha16);
  return (
    <Card hover className="h-full flex flex-col">
      <div className="flex flex-wrap items-center gap-2 mb-1.5">
        <h3 className="text-display-sm">{entry.title}</h3>
        <StatusBadge status={statusOf(entry)} />
      </div>
      {/* CO 613 — what it does, before anything about how it is classed. */}
      {entry.description && (
        <p className="mb-2 text-sm text-ink dark:text-paper" data-testid="prompt-description">
          {entry.description}
        </p>
      )}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-mute dark:text-mute-soft">
        <span>
          <span className="font-grotesk font-semibold">Class </span>
          {entry.class}
        </span>
        {entry.grade && (
          <span>
            <span className="font-grotesk font-semibold">Grade </span>
            {entry.grade}
          </span>
        )}
        {entry.admitted && (
          <span>
            <span className="font-grotesk font-semibold">Admitted </span>
            {entry.admitted}
          </span>
        )}
        {entry.door && (
          <span>
            <span className="font-grotesk font-semibold">Runs via </span>
            {entry.door === "connector" ? "MCP connector" : "cockpit"}
          </span>
        )}
      </div>
      {/* The runs-against facets — what a reader needs in order to run this.
          Rendered only where the intake file actually said so: an absent facet
          stays absent rather than being guessed from a tenant's name. */}
      {(entry.platform || entry.tools.length > 0) && (
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-mute dark:text-mute-soft">
          {entry.platform && (
            <span>
              <span className="font-grotesk font-semibold">Needs platform </span>
              <code className="font-mono">{entry.platform}</code>
            </span>
          )}
          {entry.tools.length > 0 && (
            <span>
              <span className="font-grotesk font-semibold">Tools </span>
              {entry.tools.length}
            </span>
          )}
        </div>
      )}
      {/* CO 614 — what running this prompt can touch. Derived at build time
          from the tools the prompt names, through the scope map the gateway
          enforces; the author's own "scope" line is no longer shown because it
          named one scope where the tools need several. */}
      {entry.author !== undefined && (
        <p className="mt-2 text-xs text-mute dark:text-mute-soft" data-testid="prompt-author">
          <span className="font-grotesk font-semibold">Written outside ComOS </span>
          by {entry.author ?? "an outside author"}.{" "}
          {entry.tested
            ? `Run once on a ComOS test node before listing: ${entry.tested.calls} tool call${entry.tested.calls === 1 ? "" : "s"}, all inside what it says it touches (${entry.tested.at.slice(0, 10)}). One run, by one agent.`
            : "Listed by a ComOS operator after the library lint."}
        </p>
      )}
      {entry.manifest && <ManifestLine manifest={entry.manifest} />}
      {entry.id && live && sameVersion && <LiveLine id={entry.id} live={live} />}
      {entry.sha16 && (
        <code className="mt-2 block w-fit rounded bg-mute-surface dark:bg-ink-line text-signal dark:text-signal-bright font-mono text-xs px-2 py-1">
          sha256:{entry.sha16}…
        </code>
      )}
      {entry.listing && (
        <p className="mt-3 text-xs text-flow dark:text-flow-bright">
          <span className="font-grotesk font-semibold">Storefront listing · </span>
          {entry.listing.price === 0 ? "Free" : `$${entry.listing.price}`}
          {entry.listing.validity_days
            ? ` · ${entry.listing.validity_days}-day validity`
            : ""}
        </p>
      )}

      {/* CO 547 — the prompt itself. A library you cannot read is a catalog you
          cannot order from: before this, a reader who found the right prompt
          got its metadata and had to reconstruct the procedure from tool names.
          The exporter decides what is publishable, so a withheld body is never
          in the data this renders. */}
      {entry.body ? (
        <PromptBody
          id={live?.revoked && sameVersion ? null : entry.id}
          title={entry.title}
          body={entry.body}
        />
      ) : null}

      {/* Withheld-by-policy and absent are different facts (CO 546): a reader
          told neither invents the difference. Say which, and why. */}
      {entry.withheld ? (
        <p className="mt-3 text-xs text-mute dark:text-mute-soft italic">
          {entry.withheld === "sold"
            ? "Text delivered with the entitlement, not published here."
            : "Live agent code — text not published."}
        </p>
      ) : null}
    </Card>
  );
}

type Manifest = NonNullable<Entry["manifest"]>;

// Keyed by string: the generated data is `as const`, so its effect type holds
// only the classes the current library happens to contain.
const EFFECT_LABEL: Record<string, string> = {
  reads: "Reads only",
  writes: "Writes to your tenant",
  sends: "Sends messages",
  money: "Moves money",
  admin: "Administers your account",
};

function ManifestLine({ manifest }: { manifest: Manifest }) {
  return (
    <div
      className="mt-2 text-xs text-mute dark:text-mute-soft"
      data-testid="prompt-manifest"
    >
      <p>
        <span className="font-grotesk font-semibold text-ink dark:text-paper">
          {EFFECT_LABEL[manifest.effect] ?? manifest.effect}
        </span>
        {manifest.scopes.length === 0 ? (
          <> · public tools only, no scope needed</>
        ) : (
          <>
            {" "}
            · needs{" "}
            {manifest.scopes.map((s, i) => (
              <span key={s}>
                {i > 0 && ", "}
                <code className="font-mono">{s}</code>
              </span>
            ))}
          </>
        )}
      </p>
      {manifest.step_up.length > 0 && (
        <p className="mt-1">
          {manifest.step_up.map((s, i) => (
            <span key={s}>
              {i > 0 && ", "}
              <code className="font-mono">{s}</code>
            </span>
          ))}{" "}
          {manifest.step_up.length === 1 ? "is" : "are"} granted only when you
          select {manifest.step_up.length === 1 ? "it" : "them"} on the consent
          screen at sign-in.
        </p>
      )}
    </div>
  );
}

/**
 * CO 613 — how to offer a prompt. Submitting is done the way everything else
 * here is: the reader's agent calls the tool. The line names the terms and
 * leaves acceptance to the person, not the agent.
 */
export const SUBMIT_LINE =
  'Submit a prompt I wrote to the ComOS prompt library. First fetch the Prompt Library Submission Terms with legal_get {"document": "submission"} and show them to me. If I accept, ask me for a title, a one or two sentence description of what it does, the prompt text, and example inputs for testing it, then call federation_prompt_submit with those and accept_terms: true (through federation_act if it is not in your tool list). Tell me the manifest it returns.';

function SubmitSection() {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(SUBMIT_LINE);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // The line is on screen and selectable.
    }
  };
  return (
    <Section>
      <Container width="prose">
        <SectionHead
          eyebrow="Offer your own"
          title="Submit a prompt you wrote."
          lead="Give your agent the line below. It shows you the submission terms, takes your prompt, and sends it in under your sign-in."
        />
        <pre className="mt-6 rounded bg-mute-surface dark:bg-ink-line p-3 text-xs font-mono whitespace-pre-wrap text-ink dark:text-paper">
          {SUBMIT_LINE}
        </pre>
        <button
          type="button"
          onClick={copy}
          className="mt-2 text-xs font-grotesk font-semibold text-signal dark:text-signal-bright underline"
        >
          {copied ? "Copied" : "Copy submit line"}
        </button>
        <ol className="mt-6 space-y-3 text-sm text-mute dark:text-mute-soft">
          <li>
            <span className="font-grotesk font-semibold text-ink dark:text-paper">It is checked.</span>{" "}
            Text a reader cannot see is refused, and so is an instruction to fetch
            instructions from somewhere else.
          </li>
          <li>
            <span className="font-grotesk font-semibold text-ink dark:text-paper">It is run.</span>{" "}
            ComOS runs it once on an isolated test node and records every tool it
            calls against the tools its text names.
          </li>
          <li>
            <span className="font-grotesk font-semibold text-ink dark:text-paper">A person decides.</span>{" "}
            Nothing is listed until a ComOS operator lists it. It appears under a
            pseudonym, and you can withdraw it at any time.
          </li>
        </ol>
        <p className="mt-6 text-xs text-mute dark:text-mute-soft">
          <a
            href="/legal/prompt-submission-terms.md"
            className="underline text-signal dark:text-signal-bright"
          >
            Prompt Library Submission Terms
          </a>
        </p>
      </Container>
    </Section>
  );
}

/** The verbatim prompt: collapsed by default, copyable in one action. */
/**
 * CO 614 — the run line. Pasting a prompt's text hands the agent a program
 * with the whole sign-in behind it. Pasting this line instead has the agent
 * ask ComOS for the prompt through federation_prompt_run, which narrows the
 * sign-in to the tools the prompt names before it returns a word of it.
 */
export function runLine(id: string): string {
  return `Run the ComOS library prompt ${id}: call federation_prompt_run with {"id": "${id}"} on the ComOS connector (through federation_act if it is not in your tool list), then follow the prompt it returns.`;
}

function PromptBody({ id, title, body }: { id: string | null; title: string; body: string }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedRun, setCopiedRun] = useState(false);

  const copyRunLine = async () => {
    if (!id) return;
    try {
      await navigator.clipboard.writeText(runLine(id));
      setCopiedRun(true);
      window.setTimeout(() => setCopiedRun(false), 2000);
    } catch {
      // Same rule as the text copy below: a denied clipboard is a non-event.
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(body);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be denied (permissions, insecure context). The text is
      // already on screen and selectable, so a failed copy is a non-event —
      // never a thrown error in the reader's face.
    }
  };

  return (
    <div className="mt-3">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="text-xs font-grotesk font-semibold text-signal dark:text-signal-bright underline"
        >
          {open ? "Hide prompt" : "Read the prompt"}
        </button>
        {id && (
          <button
            type="button"
            onClick={copyRunLine}
            className="text-xs font-grotesk font-semibold text-signal dark:text-signal-bright underline"
            aria-label={`Copy the run line for ${title}`}
          >
            {copiedRun ? "Copied" : "Copy run line"}
          </button>
        )}
        {open && (
          <button
            type="button"
            onClick={copy}
            className="text-xs font-grotesk text-mute dark:text-mute-soft underline"
            aria-label={`Copy the ${title} prompt`}
          >
            {copied ? "Copied" : "Copy"}
          </button>
        )}
      </div>
      {open && (
        <pre className="mt-2 max-h-96 overflow-auto rounded bg-mute-surface dark:bg-ink-line p-3 text-xs font-mono whitespace-pre-wrap text-ink dark:text-paper">
          {body}
        </pre>
      )}
    </div>
  );
}

export function PromptsPage() {
  const [params, setParams] = useSearchParams();
  const rawSort = params.get("sort") ?? DEFAULT_SORT;
  const sort: Sort = isSort(rawSort) ? rawSort : DEFAULT_SORT;
  const cls = params.get("class") ?? "";
  const door = params.get("door") ?? "";

  /** Write to the URL, dropping the param at its default so the canonical link
   * stays clean and shareable (the CO 540 setControl pattern). */
  const setControl = (name: string, value: string, fallback = "") => {
    const next = new URLSearchParams(params);
    if (value === fallback) next.delete(name);
    else next.set(name, value);
    setParams(next, { replace: true });
  };

  // CO 613 — text search. The library outgrew scanning by eye; the search
  // reads the title and the published body, so "refund" finds the prompt that
  // handles refunds even when its title does not say so. A withheld body is not
  // in this data, so it cannot be matched or leaked through a hit.
  const q = (params.get("q") ?? "").trim();
  const needle = q.toLowerCase();

  // CO 613/614 — reviews, recorded runs and revocations, read live and laid
  // over the built cards. Null until it arrives, and null if it never does.
  const [library, setLibrary] = useState<LiveLibrary | null>(null);
  const live = library?.state ?? null;
  useEffect(() => {
    let alive = true;
    fetchLiveLibrary()
      .then((m) => {
        if (alive) setLibrary(m);
      })
      .catch(() => {
        // The page stands on its build-time data.
      });
    return () => {
      alive = false;
    };
  }, []);

  // CO 613 — prompts written outside ComOS arrive with the live read and sit
  // in the same list, marked as such on the card.
  const community: Entry[] = (library?.community ?? []).map((c) => ({
    id: c.id,
    title: c.title,
    description: c.description,
    class: "operational",
    grade: null,
    status: "untested",
    sha16: c.sha256.slice(0, 16),
    category: "prompt:operational",
    admitted: c.admitted,
    listing: null,
    platform: null,
    scope: null,
    tools: [],
    door: "connector",
    body: c.body,
    withheld: null,
    manifest: c.manifest,
    author: c.author,
    tested: c.tested,
  }));
  const allEntries: readonly Entry[] = [...LIBRARY_ENTRIES, ...community];

  // CO 613 — a collection is a named, ordered set of prompt ids, read live.
  const collectionKey = params.get("collection") ?? "";
  const collection = library?.collections.find((c) => c.key === collectionKey) ?? null;

  const filtered = cls !== "" || door !== "" || q !== "" || collection !== null;
  const matched = allEntries.filter(
    (e) =>
      (collection === null || (e.id !== null && collection.ids.includes(e.id))) &&
      (cls === "" || e.class === cls) &&
      (door === "" || e.door === door) &&
      (needle === "" ||
        e.title.toLowerCase().includes(needle) ||
        (e.description ?? "").toLowerCase().includes(needle) ||
        (e.body ?? "").toLowerCase().includes(needle)),
  );
  // A collection reads in its own order unless the reader picked a sort.
  const shown =
    collection !== null && params.get("sort") === null
      ? [...matched].sort(
          (a, b) => collection.ids.indexOf(a.id ?? "") - collection.ids.indexOf(b.id ?? ""),
        )
      : [...matched].sort(compare(sort, live));

  return (
    <div className="bg-paper text-ink dark:bg-ink dark:text-paper">
      {/* Hero — what this page is and where it comes from */}
      <Section>
        <Container width="prose" className="text-center">
          <motion.div variants={stagger} initial="hidden" animate="show">
            <motion.p variants={fadeUp} className="ds-eyebrow mb-5">
              {HERO.eyebrow}
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-display font-semibold tracking-[-0.02em] text-[clamp(2rem,5vw,3.5rem)] leading-[1.08] text-balance"
            >
              {HERO.h1}
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="text-lead text-mute dark:text-mute-soft mt-6"
            >
              {HERO.lead}
            </motion.p>
          </motion.div>
        </Container>
      </Section>

      {/* CO 613 — the library sits directly under the hero. With Prompts in the
          header this page is an arrival point, and the search box and the first
          cards belong on the first screen; "how to run one" follows them. */}
      {/* The library — authored, graded, statused entries */}
      <Section tint="sunk">
        <Container>
          <SectionHead
            eyebrow="The library"
            title="Authored prompts, graded and statused."
            lead="This page is generated from the library a running ComOS tenant holds, by a builder prompt that lives in that library. Each entry carries its class, its rubric grade and its status exactly as the library records them: a prompt becomes proven only when a live run is journaled against its id."
          />

          {library && library.collections.length > 0 && (
            <div className="mt-8 flex flex-wrap items-center gap-2" aria-label="Collections">
              <span className="text-xs font-grotesk font-semibold text-mute dark:text-mute-soft">
                Collections
              </span>
              {library.collections.map((c) => {
                const on = c.key === collectionKey;
                return (
                  <button
                    key={c.key}
                    type="button"
                    title={c.description}
                    aria-pressed={on}
                    onClick={() => setControl("collection", on ? "" : c.key)}
                    className={`rounded-pill border px-3 py-1 text-xs font-grotesk ${
                      on
                        ? "border-signal bg-signal text-white"
                        : "border-mute-line dark:border-ink-line text-ink dark:text-paper"
                    }`}
                  >
                    {c.title} · {c.ids.length}
                  </button>
                );
              })}
            </div>
          )}

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <input
              type="search"
              aria-label="Search prompts"
              placeholder="Search prompts"
              value={params.get("q") ?? ""}
              onChange={(e) => setControl("q", e.target.value)}
              className="w-full sm:w-64 rounded border border-mute-line dark:border-ink-line bg-paper dark:bg-ink px-3 py-1.5 text-sm"
            />
            <label className="text-xs text-mute dark:text-mute-soft">
              <span className="font-grotesk font-semibold mr-2">Sort</span>
              <select
                aria-label="Sort prompts"
                value={sort}
                onChange={(e) => setControl("sort", e.target.value, DEFAULT_SORT)}
                className="rounded border border-mute-line dark:border-ink-line bg-paper dark:bg-ink px-2 py-1 text-xs"
              >
                {SORTS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="text-xs text-mute dark:text-mute-soft">
              <span className="font-grotesk font-semibold mr-2">Class</span>
              <select
                aria-label="Filter by class"
                value={cls}
                onChange={(e) => setControl("class", e.target.value)}
                className="rounded border border-mute-line dark:border-ink-line bg-paper dark:bg-ink px-2 py-1 text-xs"
              >
                {CLASSES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="text-xs text-mute dark:text-mute-soft">
              <span className="font-grotesk font-semibold mr-2">Door</span>
              <select
                aria-label="Filter by door"
                value={door}
                onChange={(e) => setControl("door", e.target.value)}
                className="rounded border border-mute-line dark:border-ink-line bg-paper dark:bg-ink px-2 py-1 text-xs"
              >
                {DOORS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
            </label>

            <span className="text-xs text-mute dark:text-mute-soft" role="status">
              {shown.length} of {allEntries.length}
              {filtered ? " — filtered" : ""}
            </span>

            {/* CO 548 — a count that narrows must come with a way back. The
                "Clear filters" link used to appear ONLY at zero results, so at
                "7 of 10" a reader was told three prompts were missing and given
                no way to see them and no reason why. Same defect class as a
                withheld body with no explanation (CO 546 phase 1b): state a
                fact, leave the reader to infer the cause. */}
            {filtered && (
              <button
                type="button"
                onClick={() => setParams(new URLSearchParams(), { replace: true })}
                className="text-xs text-signal dark:text-signal-bright underline"
              >
                Show all {allEntries.length}
              </button>
            )}
          </div>

          {shown.length === 0 ? (
            <p className="mt-10 text-sm text-mute dark:text-mute-soft">
              No prompts match those filters.{" "}
              <button
                type="button"
                onClick={() => setParams(new URLSearchParams(), { replace: true })}
                className="underline"
              >
                Clear filters
              </button>
              .
            </p>
          ) : (
            // ⚠️ CO 548 — this grid animates on MOUNT, not on scroll.
            // It used the shared inView preset (framer whileInView, -80px
            // margin), which renders every card at opacity:0 until it is
            // scrolled 80px INTO the viewport. Survivable when the grid sat
            // near the top; CO 547's "How to run one" section pushed it to
            // y≈1777 — below the fold on a cold load — so landing on
            // /prompts?door=connector (exactly the shareable link CO 543
            // advertises) showed 850px of blank space with seven invisible
            // cards in it. Measured with Playwright: 7/7 at opacity 0.
            // Content behind a scroll trigger is content that sometimes is not
            // there. The hero sections keep that preset; the catalog does not.
            <motion.div
              initial="hidden"
              animate="show"
              variants={stagger}
              className="mt-10 grid gap-5 md:grid-cols-2"
            >
              {shown.map((e) => (
                <motion.div key={e.id ?? e.title} variants={fadeUp}>
                  <EntryCard entry={e} live={e.id ? live?.get(e.id) : undefined} />
                </motion.div>
              ))}
            </motion.div>
          )}
        </Container>
      </Section>

      {/* CO 547 — how to actually run one. The page told a reader a prompt
          "runs via MCP connector" and never said what that meant or how to get
          one, so the reader's path ended at a card. Ron, 2026-09-13: "how would
          i use it? i find something i want to try. i open claude desktop, then
          what?" */}
      <Section>
        <Container width="prose">
          <SectionHead
            eyebrow="How to run one"
            title="Connect once, then paste."
            lead="These prompts drive ComOS through its MCP connector. Any MCP client works — Claude Desktop, Claude Code, or your own agent."
          />
          <ol className="mt-8 space-y-4 text-sm text-mute dark:text-mute-soft">
            <li>
              <span className="font-grotesk font-semibold text-ink dark:text-paper">
                1. Add the connector.
              </span>{" "}
              In Claude Desktop: Settings → Connectors → Add custom connector, with
              this URL. It authorizes over OAuth — you sign in as yourself, and the
              tools you get are the ones your manager account already holds.
              <code className="mt-2 block w-fit rounded bg-mute-surface dark:bg-ink-line text-signal dark:text-signal-bright font-mono text-xs px-2 py-1">
                https://comos-federation.com/mcp
              </code>
            </li>
            <li>
              <span className="font-grotesk font-semibold text-ink dark:text-paper">
                2. Check what the prompt needs.
              </span>{" "}
              Each card names the platform your tenant must compose and the scope you
              must hold. A prompt you lack the scope for will refuse when it runs —
              legibly, naming the scope, without doing half the work first.
            </li>
            <li>
              <span className="font-grotesk font-semibold text-ink dark:text-paper">
                3. Read it, then paste its run line.
              </span>{" "}
              Open "Read the prompt" to see exactly what it will do. Then use
              "Copy run line" and paste that into a new conversation: your agent
              asks ComOS for the prompt, and for the length of the run your
              sign-in can call only the tools that prompt names. You can still
              copy the full text and paste it yourself; that path runs with
              everything your sign-in holds. Most prompts take an input or two,
              a tenant id and often an apply/propose switch that defaults to
              proposing.
            </li>
          </ol>
          <p className="mt-6 text-xs text-mute dark:text-mute-soft">
            A prompt marked <span className="font-grotesk font-semibold">untested</span>{" "}
            has been graded but never run against a live tenant. Running one is
            useful; trusting it blindly is not.
          </p>
        </Container>
      </Section>

      {!filtered && <SubmitSection />}

      {/* Runtime-source prompts — the enrolled live-agent prompts.
          Deliberately NOT subject to the controls above: they are a different
          kind of entry (enrolled from code, gate-held, carrying no grade), and
          folding them into a class filter would invite reading "untested" and
          "gate-held" as the same state. Hidden while a filter is active so the
          filtered view answers exactly what was asked. */}
      {!filtered && (
        <Section tint="sunk">
          <Container>
            <SectionHead
              eyebrow="Runtime source"
              title="Enrolled live-agent prompts."
              lead="These are the prompts the live agent fleet actually runs, enrolled into the library from source and held to it by the CO-527 drift gate: every build re-hashes the fenced source regions against the canon, so an edited prompt cannot ship without a deliberate, reviewed snapshot change. They carry no grade or run status — their state is gate-held."
            />
            {/* CO 548 — mount-animated for the same reason as the grid above;
                this section sits even lower on the page. */}
            <motion.div
              initial="hidden"
              animate="show"
              variants={stagger}
              className="mt-10 grid gap-5 md:grid-cols-2"
            >
              {RUNTIME_ENTRIES.map((e) => (
                <motion.div key={e.id ?? e.title} variants={fadeUp}>
                  <EntryCard entry={e} />
                </motion.div>
              ))}
            </motion.div>
          </Container>
        </Section>
      )}
    </div>
  );
}
